import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ChevronDown, ChevronUp, Wheat, Shell, Egg, Fish, Sprout, Milk, Nut, Droplet, Leaf, Atom, Circle, Plus, X, ChevronLeft, ChevronRight, Check, Bell, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { OPTIONS } from "@/components/OptionsSelector";
import ShoppingCart from "@/components/ShoppingCart";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ALLERGEN_ICONS = {
  gluten: Wheat,
  crustaceos: Shell,
  huevos: Egg,
  pescado: Fish,
  cacahuetes: Circle,
  soja: Sprout,
  lacteos: Milk,
  frutos_cascara: Nut,
  apio: Leaf,
  mostaza: Droplet,
  sesamo: Atom,
  sulfitos: Atom,
  moluscos: Shell,
  altramuces: Sprout,
};

export default function PublicMenu() {
  const { menuId } = useParams();
  const [menu, setMenu] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState({});
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [currentPromoIndex, setCurrentPromoIndex] = useState(0);
  const [showPromotion, setShowPromotion] = useState(false);
  const [addedProducts, setAddedProducts] = useState(new Set());
  const [addedFromPromotion, setAddedFromPromotion] = useState(false);
  const [events, setEvents] = useState([]);
  const [currentEventIndex, setCurrentEventIndex] = useState(0);
  const [showEvents, setShowEvents] = useState(false);

  useEffect(() => {
    fetchMenuData();
    // Load cart from localStorage
    const savedCart = localStorage.getItem(`cart-${menuId}`);
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  }, [menuId]);

  useEffect(() => {
    // Save cart to localStorage whenever it changes
    if (menuId) {
      localStorage.setItem(`cart-${menuId}`, JSON.stringify(cart));
    }
  }, [cart, menuId]);

  const fetchMenuData = async () => {
    try {
      setLoading(true);
      const menuResponse = await axios.get(`${API}/menus/${menuId}`);
      setMenu(menuResponse.data);

      const categoriesResponse = await axios.get(`${API}/menus/${menuId}/categories`);
      setCategories(categoriesResponse.data);

      const productsData = {};
      for (const category of categoriesResponse.data) {
        const productsResponse = await axios.get(`${API}/categories/${category.id}/products`);
        productsData[category.id] = productsResponse.data;
      }
      setProducts(productsData);

      // Fetch promotions
      const promotionResponse = await axios.get(`${API}/menus/${menuId}/promotion`);
      if (promotionResponse.data && promotionResponse.data.length > 0) {
        setPromotions(promotionResponse.data);
        // Show promotion only once per session
        const promotionSeen = sessionStorage.getItem(`promotion-seen-${menuId}`);
        if (!promotionSeen) {
          setShowPromotion(true);
        }
      }

      // Fetch events
      const eventsResponse = await axios.get(`${API}/menus/${menuId}/events`);
      if (eventsResponse.data && eventsResponse.data.length > 0) {
        setEvents(eventsResponse.data);
      }
    } catch (error) {
      console.error("Error fetching menu data:", error);
    } finally {
      setLoading(false);
    }
  };

  const closePromotion = () => {
    setShowPromotion(false);
    sessionStorage.setItem(`promotion-seen-${menuId}`, 'true');
  };

  const addPromotionToCart = () => {
    const currentPromo = promotions[currentPromoIndex];
    if (currentPromo?.product) {
      // Use promotional price if available
      const productWithPromoPrice = {
        ...currentPromo.product,
        price: currentPromo.promotional_price !== null && currentPromo.promotional_price !== undefined 
          ? currentPromo.promotional_price 
          : currentPromo.product.price
      };
      addToCart(productWithPromoPrice);
      // DON'T close promotion - let user continue browsing offers
      
      // Show animation on promotion button
      setAddedFromPromotion(true);
      setTimeout(() => {
        setAddedFromPromotion(false);
      }, 1000);
    }
  };
  
  const reopenPromotions = () => {
    if (promotions.length > 0) {
      setShowPromotion(true);
    }
  };

  const openEvents = () => {
    if (events.length > 0) {
      setShowEvents(true);
    }
  };

  const closeEvents = () => {
    setShowEvents(false);
  };

  const nextEvent = () => {
    setCurrentEventIndex((prev) => (prev + 1) % events.length);
  };

  const prevEvent = () => {
    setCurrentEventIndex((prev) => (prev - 1 + events.length) % events.length);
  };

  // Check if a product has an active promotion with special price
  const getPromotionalPrice = (productId) => {
    const activePromo = promotions.find(promo => 
      promo.product?.id === productId && 
      promo.promotional_price !== null && 
      promo.promotional_price !== undefined
    );
    return activePromo ? activePromo.promotional_price : null;
  };

  const nextPromotion = () => {
    setCurrentPromoIndex((prev) => (prev + 1) % promotions.length);
  };

  const prevPromotion = () => {
    setCurrentPromoIndex((prev) => (prev - 1 + promotions.length) % promotions.length);
  };

  const toggleCategory = (categoryId) => {
    const isExpanding = expandedCategory !== categoryId;
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
    
    // If expanding, scroll to the category after a short delay to let it expand
    if (isExpanding) {
      setTimeout(() => {
        const categoryElement = document.querySelector(`[data-testid="category-${categoryId}"]`);
        if (categoryElement) {
          const headerOffset = 20; // Small offset from top
          const elementPosition = categoryElement.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100); // Small delay to let accordion expand
    }
  };

  const addToCart = (product) => {
    const existingItem = cart.find(item => item.id === product.id);
    if (existingItem) {
      setCart(cart.map(item =>
        item.id === product.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
      toast.success(`${product.name} cantidad aumentada`);
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
      toast.success(`${product.name} añadido al carrito`);
    }
    
    // Add visual feedback - show check icon temporarily
    setAddedProducts(prev => new Set(prev).add(product.id));
    setTimeout(() => {
      setAddedProducts(prev => {
        const newSet = new Set(prev);
        newSet.delete(product.id);
        return newSet;
      });
    }, 1000); // Show for 1 second
  };

  const goToTop = () => {
    // Colapsar todas las categorías
    setExpandedCategory(null);
    // Scroll suave al inicio
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart(cart.map(item =>
      item.id === productId
        ? { ...item, quantity: newQuantity }
        : item
    ));
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.id !== productId));
    toast.success("Producto eliminado del carrito");
  };

  const clearCart = () => {
    setCart([]);
    toast.success("Carrito vaciado");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Cargando menú...</p>
        </div>
      </div>
    );
  }

  if (!menu) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Menú no encontrado</h1>
          <p className="text-gray-600">El menú solicitado no existe</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50 relative">
      {/* Botón Home - Volver al inicio */}
      <div className="fixed bottom-4 left-4 z-50">
        <div className="relative group">
          <button
            onClick={goToTop}
            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full w-14 h-14 shadow-lg hover:shadow-xl transition-all hover:scale-110 flex items-center justify-center"
            aria-label="Volver al inicio"
          >
            <Home className="w-6 h-6" />
          </button>
          {/* Tooltip */}
          <span className="absolute left-full ml-2 top-1/2 -translate-y-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            Volver al inicio
          </span>
        </div>
      </div>

      {/* Header with Logo Banner */}
      {menu.logo_url ? (
        <div className="relative w-full bg-gradient-to-br from-white to-emerald-50 shadow-md overflow-hidden" data-testid="menu-header">
          {/* Logo Banner */}
          <div className="relative h-32 sm:h-40 flex items-center justify-center px-4 py-6">
            {/* Logo */}
            <img 
              src={menu.logo_url.startsWith('http') ? menu.logo_url : `${BACKEND_URL}${menu.logo_url}`}
              alt={menu.name}
              className="relative z-10 h-20 sm:h-28 w-auto object-contain drop-shadow-lg"
              data-testid="menu-logo"
            />
          </div>
          
          {/* Menu Name */}
          <div className="bg-white bg-opacity-90 backdrop-blur-sm py-4 px-4 text-center border-t border-emerald-100">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">
              {menu.name}
            </h1>
            <div className="w-16 h-1 bg-emerald-600 mx-auto mt-3 rounded-full"></div>
          </div>
        </div>
      ) : (
        // Fallback if no logo
        <div className="bg-gradient-to-br from-white to-emerald-50 shadow-md">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center" data-testid="menu-header">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3">
              {menu.name}
            </h1>
            <div className="w-20 h-1 bg-emerald-600 mx-auto rounded-full"></div>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Categories */}
        {categories.length > 0 ? (
          <div className="space-y-3" data-testid="categories-list">
            {categories.map((category) => (
              <div key={category.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-testid={`category-${category.id}`}>
                <button
                  data-testid={`category-button-${category.id}`}
                  onClick={() => toggleCategory(category.id)}
                  className="w-full px-5 py-5 sm:py-4 flex items-center justify-between hover:bg-emerald-50 transition-colors active:bg-emerald-100 touch-manipulation"
                >
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{category.name}</h2>
                  {expandedCategory === category.id ? (
                    <ChevronUp className="w-7 h-7 sm:w-6 sm:h-6 text-emerald-600 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-7 h-7 sm:w-6 sm:h-6 text-emerald-600 flex-shrink-0" />
                  )}
                </button>

                {expandedCategory === category.id && (
                  <div className="px-4 pb-4 pt-2 bg-white" data-testid={`products-${category.id}`}>
                    <div className="space-y-3">
                      {(products[category.id] || []).map((product) => (
                        <div
                          key={product.id}
                          data-testid={`product-${product.id}`}
                          className="p-4 rounded-lg border border-gray-200 hover:border-emerald-300 hover:shadow-md transition-all bg-white"
                        >
                          <div className="flex flex-col sm:flex-row gap-3">
                            {product.image_url && (
                              <img
                                src={product.image_url.startsWith('http') ? product.image_url : `${BACKEND_URL}${product.image_url}`}
                                alt={product.name}
                                className="w-full sm:w-20 h-32 sm:h-20 object-cover rounded-lg flex-shrink-0"
                                data-testid={`product-image-${product.id}`}
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              {/* Product name - full width */}
                              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 leading-tight">
                                {product.name}
                              </h3>
                              
                              {product.description && (
                                <p className="text-sm text-gray-600 leading-relaxed mb-3">
                                  {product.description}
                                </p>
                              )}

                              {/* Options badges */}
                              {product.options && product.options.length > 0 && (
                                <div className="flex gap-1 mb-3 flex-wrap">
                                  {product.options.map((optId) => {
                                    const option = OPTIONS.find(o => o.id === optId);
                                    if (!option) return null;
                                    const Icon = option.icon;
                                    return (
                                      <span key={optId} className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-gray-100 ${option.color}`}>
                                        <Icon className="w-3 h-3" />
                                        {option.label}
                                      </span>
                                    );
                                  })}
                                </div>
                              )}

                              {/* Price and add button - separate row */}
                              <div className="flex items-center justify-between gap-3 pt-2 border-t border-gray-100">
                                {(() => {
                                  const promoPrice = getPromotionalPrice(product.id);
                                  return promoPrice !== null ? (
                                    <div className="flex items-center gap-2">
                                      {product.price > 0 && (
                                        <span className="text-base text-gray-400 line-through">
                                          {product.price.toFixed(2)}€
                                        </span>
                                      )}
                                      <span className="text-xl font-bold text-red-600">
                                        {promoPrice.toFixed(2)}€
                                      </span>
                                      <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded">
                                        OFERTA
                                      </span>
                                    </div>
                                  ) : (
                                    product.price > 0 ? (
                                      <span className="text-xl font-bold text-emerald-600">
                                        {product.price.toFixed(2)}€
                                      </span>
                                    ) : (
                                      <span />
                                    )
                                  );
                                })()}
                                <button
                                  data-testid={`add-to-cart-${product.id}`}
                                  onClick={() => {
                                    const promoPrice = getPromotionalPrice(product.id);
                                    const productToAdd = promoPrice !== null 
                                      ? { ...product, price: promoPrice }
                                      : product;
                                    addToCart(productToAdd);
                                  }}
                                  className={`w-10 h-10 flex items-center justify-center text-white rounded-lg shadow-md hover:shadow-lg flex-shrink-0 transition-all duration-300 ${
                                    addedProducts.has(product.id) 
                                      ? 'bg-green-500 scale-110' 
                                      : 'bg-emerald-600 hover:bg-emerald-700 hover:scale-105'
                                  }`}
                                >
                                  {addedProducts.has(product.id) ? (
                                    <Check className="w-5 h-5 animate-in zoom-in duration-200" />
                                  ) : (
                                    <Plus className="w-5 h-5" />
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                          
                          {/* Allergens */}
                          {((product.allergens && product.allergens.length > 0) || (product.traces && product.traces.length > 0)) && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                                  {product.allergens && product.allergens.length > 0 && (
                                    <div className="mb-2">
                                      <p className="text-xs font-semibold text-gray-700 mb-1">Alérgenos:</p>
                                      <div className="flex gap-1 flex-wrap">
                                        {product.allergens.map((allergenId) => {
                                          const Icon = ALLERGEN_ICONS[allergenId] || Atom;
                                          return (
                                            <span key={allergenId} className="flex items-center gap-1 text-xs bg-red-50 text-red-700 px-2 py-1 rounded">
                                              <Icon className="w-3 h-3" />
                                              <span className="whitespace-nowrap">{allergenId}</span>
                                            </span>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                  {product.traces && product.traces.length > 0 && (
                                    <div>
                                      <p className="text-xs font-semibold text-gray-700 mb-1">Trazas:</p>
                                      <div className="flex gap-1 flex-wrap">
                                        {product.traces.map((traceId) => {
                                          const Icon = ALLERGEN_ICONS[traceId] || Atom;
                                          return (
                                            <span key={traceId} className="flex items-center gap-1 text-xs bg-amber-50 text-amber-700 px-2 py-1 rounded">
                                              <Icon className="w-3 h-3" />
                                              <span className="whitespace-nowrap">{traceId}</span>
                                            </span>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No hay productos disponibles en este momento</p>
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-10 pt-6 border-t border-gray-200">
          <p className="text-sm text-gray-500">Menú digital - {menu.name}</p>
        </div>
      </div>

      {/* Events Button - only show if there are events and button is enabled */}
      {events.length > 0 && menu.show_events_button !== false && (
        <div className="fixed bottom-[136px] right-4 z-40">
          <button
            onClick={openEvents}
            aria-label="Ver eventos"
            className="bg-green-500/90 hover:bg-green-600 backdrop-blur-sm text-white rounded-full w-[52px] h-[52px] shadow-lg hover:shadow-xl transition-all hover:scale-105 flex items-center justify-center text-2xl"
          >
            💃
          </button>
        </div>
      )}

      {/* Promotions Button - only show if there are promotions */}
      {promotions.length > 0 && (
        <div className="fixed bottom-[76px] right-4 z-40">
          <button
            onClick={reopenPromotions}
            aria-label="Ver promociones"
            className="bg-red-500/90 hover:bg-red-600 backdrop-blur-sm text-white rounded-full w-[52px] h-[52px] shadow-lg hover:shadow-xl transition-all hover:scale-105 flex items-center justify-center"
          >
            <Bell className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Shopping Cart */}
      <ShoppingCart
        cart={cart}
        onUpdateQuantity={updateQuantity}
        onRemoveItem={removeFromCart}
        onClearCart={clearCart}
        showTotal={menu.show_cart_total !== false}
      />

      {/* Promotion Pop-up Carousel */}
      {promotions.length > 0 && (
        <Dialog open={showPromotion} onOpenChange={closePromotion}>
          <DialogContent className="max-w-[90%] sm:max-w-lg p-0 overflow-hidden rounded-3xl border-none shadow-2xl">
            {/* Visually hidden title for accessibility */}
            <DialogTitle className="sr-only">Promociones Destacadas</DialogTitle>
            <DialogDescription className="sr-only">
              {promotions[currentPromoIndex]?.description || promotions[currentPromoIndex]?.title || "Promoción especial del restaurante"}
            </DialogDescription>
            
            {/* Close button */}
            <button
              onClick={closePromotion}
              aria-label="Cerrar promoción"
              className="absolute top-4 right-4 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-white/95 hover:bg-white shadow-lg transition-all hover:scale-110"
            >
              <X className="w-5 h-5 text-gray-700" />
            </button>

            {/* Navigation Arrows - only if multiple promotions - positioned at image level */}
            {promotions.length > 1 && (
              <>
                <button
                  onClick={prevPromotion}
                  aria-label="Promoción anterior"
                  className="absolute left-3 top-24 z-20 w-11 h-11 flex items-center justify-center rounded-full bg-white/95 hover:bg-white shadow-lg transition-all hover:scale-110"
                >
                  <ChevronLeft className="w-6 h-6 text-gray-700" />
                </button>
                <button
                  onClick={nextPromotion}
                  aria-label="Siguiente promoción"
                  className="absolute right-3 top-24 z-20 w-11 h-11 flex items-center justify-center rounded-full bg-white/95 hover:bg-white shadow-lg transition-all hover:scale-110"
                >
                  <ChevronRight className="w-6 h-6 text-gray-700" />
                </button>
              </>
            )}

            {/* Current Promotion */}
            {promotions[currentPromoIndex] && (
              <>
                {/* Promotion Image */}
                {promotions[currentPromoIndex].image_url && (
                  <div className="w-full h-48 sm:h-56 overflow-hidden bg-gradient-to-br from-emerald-50 to-green-50">
                    <img
                      src={promotions[currentPromoIndex].image_url.startsWith('http') 
                        ? promotions[currentPromoIndex].image_url 
                        : `${BACKEND_URL}${promotions[currentPromoIndex].image_url}`}
                      alt={promotions[currentPromoIndex].title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                {/* Promotion Content */}
                <div className="p-6 sm:p-8">
                  <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
                    {promotions[currentPromoIndex].title}
                  </h3>
                  {promotions[currentPromoIndex].description && (
                    <p className="text-gray-600 leading-relaxed mb-4 text-base sm:text-lg">
                      {promotions[currentPromoIndex].description}
                    </p>
                  )}

                  {/* Price Display */}
                  {promotions[currentPromoIndex].product && (
                    <div className="mb-6">
                      {promotions[currentPromoIndex].promotional_price !== null && 
                       promotions[currentPromoIndex].promotional_price !== undefined ? (
                        <div className="flex items-center justify-center gap-3">
                          <span className="text-xl text-gray-400 line-through">
                            {promotions[currentPromoIndex].product.price.toFixed(2)}€
                          </span>
                          <span className="text-3xl font-bold text-emerald-600">
                            {promotions[currentPromoIndex].promotional_price.toFixed(2)}€
                          </span>
                          <span className="bg-red-500 text-white text-sm font-bold px-2 py-1 rounded-lg">
                            ¡OFERTA!
                          </span>
                        </div>
                      ) : (
                        <div className="text-center">
                          <span className="text-3xl font-bold text-emerald-600">
                            {promotions[currentPromoIndex].product.price.toFixed(2)}€
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Action Button */}
                  {promotions[currentPromoIndex].product && (
                    <Button
                      onClick={addPromotionToCart}
                      className={`w-full text-white h-12 sm:h-14 text-base sm:text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 ${
                        addedFromPromotion 
                          ? 'bg-green-500 scale-105' 
                          : 'bg-emerald-600 hover:bg-emerald-700'
                      }`}
                    >
                      {addedFromPromotion ? (
                        <>
                          <Check className="w-5 h-5 mr-2 animate-in zoom-in duration-200" />
                          ¡Añadido!
                        </>
                      ) : (
                        <>
                          <Plus className="w-5 h-5 mr-2" />
                          Añadir al Carrito
                        </>
                      )}
                    </Button>
                  )}

                  {/* Dots Indicator - only if multiple promotions */}
                  {promotions.length > 1 && (
                    <div className="flex justify-center gap-2 mt-6">
                      {promotions.map((promo, index) => (
                        <button
                          key={promo.id || index}
                          onClick={() => setCurrentPromoIndex(index)}
                          className={`w-2 h-2 rounded-full transition-all ${
                            index === currentPromoIndex 
                              ? 'bg-emerald-600 w-6' 
                              : 'bg-gray-300 hover:bg-gray-400'
                          }`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Events Modal Carousel */}
      {events.length > 0 && (
        <Dialog open={showEvents} onOpenChange={closeEvents}>
          <DialogContent className="max-w-[90%] sm:max-w-lg p-0 overflow-hidden rounded-3xl border-none shadow-2xl">
            {/* Visually hidden title for accessibility */}
            <DialogTitle className="sr-only">Eventos y Conciertos</DialogTitle>
            <DialogDescription className="sr-only">
              {events[currentEventIndex]?.description || events[currentEventIndex]?.title || "Eventos del restaurante"}
            </DialogDescription>
            
            {/* Close button */}
            <button
              onClick={closeEvents}
              aria-label="Cerrar eventos"
              className="absolute top-4 right-4 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-white/95 hover:bg-white shadow-lg transition-all hover:scale-110"
            >
              <X className="w-5 h-5 text-gray-700" />
            </button>

            {/* Navigation Arrows - only if multiple events */}
            {events.length > 1 && (
              <>
                <button
                  onClick={prevEvent}
                  aria-label="Evento anterior"
                  className="absolute left-3 top-24 z-20 w-11 h-11 flex items-center justify-center rounded-full bg-white/95 hover:bg-white shadow-lg transition-all hover:scale-110"
                >
                  <ChevronLeft className="w-6 h-6 text-gray-700" />
                </button>
                <button
                  onClick={nextEvent}
                  aria-label="Siguiente evento"
                  className="absolute right-3 top-24 z-20 w-11 h-11 flex items-center justify-center rounded-full bg-white/95 hover:bg-white shadow-lg transition-all hover:scale-110"
                >
                  <ChevronRight className="w-6 h-6 text-gray-700" />
                </button>
              </>
            )}

            {/* Current Event */}
            {events[currentEventIndex] && (
              <>
                {/* Event Image */}
                {events[currentEventIndex].image_url && (
                  <div className="w-full h-48 sm:h-56 overflow-hidden bg-gradient-to-br from-green-50 to-emerald-50">
                    <img
                      src={events[currentEventIndex].image_url.startsWith('http') 
                        ? events[currentEventIndex].image_url 
                        : `${BACKEND_URL}${events[currentEventIndex].image_url}`}
                      alt={events[currentEventIndex].title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                {/* Event Content */}
                <div className="p-6 sm:p-8">
                  <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
                    {events[currentEventIndex].title}
                  </h3>
                  {events[currentEventIndex].description && (
                    <p className="text-gray-600 leading-relaxed mb-4 text-base sm:text-lg">
                      {events[currentEventIndex].description}
                    </p>
                  )}

                  {/* Date Display */}
                  {(events[currentEventIndex].start_date || events[currentEventIndex].end_date) && (
                    <div className="mb-6 p-4 bg-green-50 rounded-xl">
                      <div className="text-sm text-green-800">
                        {events[currentEventIndex].start_date && (
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold">Inicio:</span>
                            <span>{new Date(events[currentEventIndex].start_date).toLocaleDateString('es-ES', { 
                              day: 'numeric', 
                              month: 'long', 
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}</span>
                          </div>
                        )}
                        {events[currentEventIndex].end_date && (
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">Fin:</span>
                            <span>{new Date(events[currentEventIndex].end_date).toLocaleDateString('es-ES', { 
                              day: 'numeric', 
                              month: 'long', 
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Dots Indicator - only if multiple events */}
                  {events.length > 1 && (
                    <div className="flex justify-center gap-2 mt-6">
                      {events.map((evt, index) => (
                        <button
                          key={evt.id || index}
                          onClick={() => setCurrentEventIndex(index)}
                          className={`w-2 h-2 rounded-full transition-all ${
                            index === currentEventIndex 
                              ? 'bg-green-600 w-6' 
                              : 'bg-gray-300 hover:bg-gray-400'
                          }`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}